import { SerializedData, Store } from "bernie-core";
import { action, observable } from "mobx";
import {
  Composition,
  ExtendedContextStore,
  FlexContext,
  ViewModelResponse,
  AjaxParams,
  AjaxQueryParams
} from "typings/flexFramework/FlexDefinitions";
import * as fetch from "isomorphic-fetch";
import { safeResponseToJson } from "components/utility/FetchUtils";
import { Logger, NOOP_LOGGER } from "bernie-logger";
import { FlexComponent } from "src/typings/flexFramework/FlexComponent";
import {
  FAILED_REACTIVE_MODULES_COMPOSITION_FETCH,
  FAILED_MODULE_COMPOSITION_FETCH
} from "config/systemEvents";
import { EditorialFlexComponent } from "src/components/flexComponents/Editorial/typings";
import { getQueryParamValue } from "components/utility/FeatureFlag/UrlUtils";
import { StaticMapStore } from "stores/staticMap/StaticMapStore";

type IdentifierKey = "id" | "name" | "fmId";

export class CompositionStore extends Store {
  public viewModel: ViewModelResponse;

  @observable
  public composition: Composition;
  public templateId: string;
  public urlMappingId: string;
  public flexContext: FlexContext;

  public constructor(state: SerializedData = {}, logger: Logger = NOOP_LOGGER) {
    super(state, logger);
  }

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  @action
  public setupCompositionStore(
    viewModel: ViewModelResponse,
    context: ExtendedContextStore,
    mapStore: StaticMapStore
  ) {
    if (viewModel) {
      this.viewModel = viewModel;
      this.composition = viewModel.composition;
      this.templateId = viewModel.templateId;
      this.urlMappingId = viewModel.flexContext.searchContext.urlMappingId;
      this.flexContext = viewModel.flexContext;

      this.injectInterestFreePayLater(
        viewModel.flexContext.siteContext.tpid,
        viewModel.flexContext
      );

      this.buildStaticMapImgUrl(context, mapStore);
    }
  }

  public buildStaticMapImgUrl = (context: ExtendedContextStore, mapStore: StaticMapStore) => {
    if (!this.composition) {
      return;
    }

    const countryCode = context && context.site && context.site.country;
    for (const regionList of this.composition.page.regionList as FlexComponent[]) {
      const hotels = findComponentByIdOrNameInComposition(
        regionList,
        "id",
        "hotels-1" // First hotel in composition
      );
      if (hotels) {
        mapStore.generateGoogleStaticMapSrcForHotels(
          context,
          countryCode ? countryCode : "US",
          hotels.model.hotels
        );
        break;
      }
    }
  };

  /**
   * Trigers the ajax call to lpt-flex-web to request a certain module by its module id
   * @param fmId a string id representing the module id
   * @param locationUserId new location value, this will be updated with the passed context.
   * @returns the ajax response model
   */
  public fetchModelFromFmId = async (fmId: number, locationUserId?: string) => {
    const options = {
      tId: this.templateId,
      fmId,
      url: this.buildUrlQueryParam(),
      cache: "false",
      epoch: Date.now().toString(),
      viewName: "json",
      clientId: "blossom",
      userAttributes: "blossom"
    };

    const specificParams = {
      locationUserId,
      path: "/lpt/flex/ajax",
      event: FAILED_MODULE_COMPOSITION_FETCH,
      loggerArgs: `Error when fetching composition for module due to missing values for templateId=${
        this.templateId
      }, fmId=${fmId}`
    };

    const endpointUrl = this.buildAjaxEndpointUrl(options, specificParams);

    if (!endpointUrl || !fmId) {
      return;
    }

    return fetch(endpointUrl, { method: "GET" }).then(safeResponseToJson);
  };

  /**
   * Trigers the ajax call to lpt-flex-web to request modules that have a scene attribute,
   * ajax will depend on the passed sceneId param to return values for the modules that belong to the passed scene
   * and to return empty and set ajaxOnly=true to the modules that belong to a diff scene.
   * @param sceneId a string id representing the scene attribute value
   * @returns the ajax response model
   */
  private fetchModelFromSceneId = async (sceneId: string) => {
    const options = {
      tId: this.templateId,
      scene: sceneId
    };

    const specificParams = {
      path: "/lpt/flex/reactive",
      event: FAILED_REACTIVE_MODULES_COMPOSITION_FETCH,
      loggerArgs: `Error when fetching reactive modules for scene due to missing values for templateId=${
        this.templateId
      }, scene=${sceneId}`
    };

    const endpointUrl = this.buildAjaxEndpointUrl(options, specificParams);

    if (!endpointUrl || !sceneId) {
      return;
    }

    return fetch(endpointUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify(this.flexContext)
    }).then(safeResponseToJson);
  };

  /**
   * fetches modules that belong to a scene, and update of the composition
   * the ajax will return values for the modules that belong to the passed scene.
   * and will return empty and set ajaxOnly=true to the modules that belong to a diff scene.
   * @param scene a string id representing the scene attribute value
   */
  public fetchModelAndUpdateCompositionFromSceneId = async (scene: string) => {
    return this.fetchModelFromSceneId(scene)
      .then((components: FlexComponent[]) => {
        const regions =
          this.composition && this.composition.page && this.composition.page.regionList;

        for (const component of components) {
          if (component) {
            this.updateCompositionFromFmId(regions, component.fmId, "fmId", component);
          }
        }
      })

      .catch(err => {
        this.logger.logEvent(
          FAILED_MODULE_COMPOSITION_FETCH,
          "Unexpected error when fetching composition for scene",
          err
        );
      });
  };

  /**
   * given the new value for the component this method will update its value in the stored composition.
   * @param regions the composition model
   * @param keyValue the id, name, fmId of the component to be updated
   * @param key determains whether to update the component by its id,name,fmId
   * @param component the new component to be updated:
   * incase only the model is sent it will update the model of the component in the composition that has the same passed id,
   * incase the entire flexComponnet is sent it will fetch the model form it and update the model of the component in the composition that has the same passed id
   */
  private updateCompositionFromFmId = (
    regions: FlexComponent[],
    keyValue: string | number,
    key: IdentifierKey,
    component: FlexComponent
  ) => {
    let componentToUpdate: FlexComponent | undefined;
    regions.find(region => {
      if (region) {
        componentToUpdate = findComponentByIdOrNameInComposition(region, key, keyValue);
      }

      return !!componentToUpdate;
    });

    if (componentToUpdate) {
      componentToUpdate.model = component && component.model ? component.model : component;
    }

    return;
  };

  /**
   * fetches the module that has the passed fmId
   * the ajax will return the new model for the requested module.
   * this method will trigger the update of the composition based on the returned ajax response.
   * @param fmId a string representing the module id
   * @param locationUserId new location value, this will be updated with the passed context.
   */
  public fetchModelAndUpdateCompositionFromFmId = async (fmId: number, locationUserId?: string) => {
    return this.fetchModelFromFmId(fmId, locationUserId)
      .then((moduleBody: FlexComponent) => {
        if (moduleBody) {
          const regions =
            this.composition && this.composition.page && this.composition.page.regionList;
          if (!regions) {
            return Promise.resolve({
              status: 500
            });
          }
          this.updateCompositionFromFmId(regions, fmId, "fmId", moduleBody);
        }

        return Promise.resolve({
          status: 200
        });
      })
      .catch(err => {
        this.logger.logEvent(
          FAILED_MODULE_COMPOSITION_FETCH,
          "Unexpected error when fetching composition for module",
          err
        );

        return Promise.resolve({
          status: 500
        });
      });
  };

  /**
   * finds out if the region should be displayed or not based on :
   * if all its children are ajax only then it will be hidden
   * @param regionid
   */
  public shouldDisplayRegion(regionFmId: number) {
    let shouldDisplayRegion = false;
    let requestedRegion: FlexComponent | undefined;
    const regions = this.composition && this.composition.page && this.composition.page.regionList;
    requestedRegion = findRegionByIdInComposition(regions, "fmId", regionFmId);
    if (requestedRegion) {
      requestedRegion.modules.forEach(module => {
        if (module.model.ajaxOnly !== true) {
          shouldDisplayRegion = true;
        }
      });
    }

    return shouldDisplayRegion;
  }

  private buildAjaxEndpointUrl(options: AjaxQueryParams, specificParams: AjaxParams) {
    if (!options.tId || (!options.fmId && !options.scene)) {
      this.logger.logEvent(
        specificParams.event,
        specificParams.loggerArgs,
        JSON.stringify(options)
      );

      return;
    }

    if (specificParams.locationUserId) {
      options["locationUserId"] = specificParams.locationUserId;
    }

    let queryParams = "";
    Object.keys(options)
      .filter(key => options[key])
      .forEach((key, index) => {
        if (key) {
          queryParams =
            index === 0 ? `?${key}=${options[key]}` : `${queryParams}&${key}=${options[key]}`;
        }
      });

    return `${specificParams.path}${queryParams}`;
  }

  private buildUrlQueryParam(): string | undefined {
    if (this.urlMappingId) {
      const tName = getQueryParamValue("tName");
      let urlQueryParam = this.urlMappingId;

      if (this.urlMappingId.startsWith("/")) {
        urlQueryParam = this.urlMappingId.substr(1);
      }

      if (tName) {
        urlQueryParam = `${urlQueryParam}?tName=${tName}`;
      }

      return encodeURI(urlQueryParam);
    }

    return undefined;
  }

  // Temporary hack to add required EXP_MX (TPID 12) /EXP_BR (TPID 69) Legal & Loyalty Information
  // This assumes that the region is called Wizard and page type is HTG
  private injectInterestFreePayLater = (tpid: number, flexContext: FlexContext) => {
    if (
      (tpid === 12 || tpid === 69) &&
      flexContext &&
      flexContext.searchContext &&
      flexContext.searchContext.pageType === "Travel-Guide-Hotels"
    ) {
      const wizardRegion =
        this.composition &&
        this.composition.page &&
        this.composition.page.regionList &&
        this.composition.page.regionList.filter(region => region.name === "wizard")[0];

      if (wizardRegion) {
        const editorials: EditorialFlexComponent[] = wizardRegion && wizardRegion.modules;
        const messaging = tpid === 12 ? "Hoteles en hasta 18 MSI" : "Hotéis em até 12x sem juros";

        const injectedflexComponent: EditorialFlexComponent = {
          model: {
            contentPurpose: "ConfidenceMessage",
            items: [{ text: messaging }]
          },
          type: "Module",
          name: "editorial"
        } as any;
        wizardRegion.modules = [...editorials, injectedflexComponent];
      }
    }
  };
}

/**
 * Recurses over the composition until it finds a component
 * @param component a flex component, could be either a region or a module
 * @param key the identifier used to search the component, i.e. id or name
 * @param keyValue the identifier value
 */
export function findComponentByIdOrNameInComposition(
  component: FlexComponent,
  key: IdentifierKey,
  keyValue: string | number
): FlexComponent | undefined {
  if (component[key] === keyValue) {
    return component;
  }

  if (component.type === "Region" && component.modules) {
    let recursionResult;
    for (const childComponent of component.modules) {
      recursionResult = findComponentByIdOrNameInComposition(
        childComponent as FlexComponent,
        key,
        keyValue
      );
      if (recursionResult) {
        return recursionResult;
      }
    }
  }

  return undefined;
}

/**
 * Recurses over the composition until it finds a region
 * @param component a flex component array representing regions
 * @param key the identifier used to search the component, i.e. id or name
 * @param keyValue the identifier value
 */
export function findRegionByIdInComposition(
  regions: FlexComponent[],
  key: IdentifierKey,
  keyValue: string | number
): FlexComponent | undefined {
  let requestedRegion: FlexComponent | undefined;
  regions.forEach(region => {
    const foundRegion = findComponentByIdOrNameInComposition(region, "fmId", keyValue);
    if (foundRegion && foundRegion.model[key] === keyValue) {
      requestedRegion = foundRegion;
    }
  });

  return requestedRegion;
}
